import { captureRemixErrorBoundaryError, setUser } from "@sentry/remix";
import * as React from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useLoaderData,
  isRouteErrorResponse,
  useLocation,
} from "@remix-run/react";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import { redirect } from "@remix-run/node";
import type { ShouldRevalidateFunctionArgs } from "@remix-run/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getEnv } from "~server/lib/env";
import { ProgressProvider } from "@bprogress/remix";
import { ErrorPage } from "./components/ui/error-page";
import tailwindStyles from "./tailwind.css?url";
import { loadBasicUser } from "~server/utils/loader-context.server";
import { bindLoaderReadScope } from "~server/utils/request-scope";
import {
  CHANGE_PASSWORD_PATH,
  gateFor,
} from "~server/services/auth/password-change-required.server";
import { NavigationHistoryProvider } from "~app/contexts/navigation-history-context";
import { useNavigationHistory } from "~app/hooks/use-navigation-history";
import {
  initAmplitude,
  setUserId,
  setUserProperties,
} from "~app/lib/amplitude";
import { Toaster as ThemedToaster } from "~app/components/ui/sonner";
import { ThemeProvider } from "~app/components/theme-provider";
import { FeedbackProvider } from "~app/components/feedback/feedback-dialog";

// ⭐ JumpCannon type (design system tokens/fonts.css, 28 Sep 2026): Spectral,
// IBM Plex Sans, IBM Plex Mono and Caveat from Google Fonts, the same URL.
const JC_FONTS =
  "https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,500;0,600;1,500&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&family=Caveat:wght@500&display=swap";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  { rel: "stylesheet", href: JC_FONTS },
  { rel: "stylesheet", href: tailwindStyles },
  {
    rel: "icon",
    type: "image/svg+xml",
    href: "/branding/jumpcannon/favicon-tile.svg",
  },
  { rel: "alternate icon", href: "/favicon.ico" },
  { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
];

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
    },
  },
});

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
          enableSystem
          disableTransitionOnChange
        >
          <ProgressProvider
            startOnLoad
            color="var(--brand-400)"
            height="3px"
            options={{
              showSpinner: false,
            }}
          >
            {children}
          </ProgressProvider>
          <ThemedToaster />
        </ThemeProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  // ⭐ what threw, kept on the server — Sentry is not configured on DEV
  React.useEffect(() => {
    try {
      const e = error as {
        message?: unknown;
        stack?: unknown;
        status?: unknown;
      };
      void fetch("/client-error", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          kind: isRouteErrorResponse(error)
            ? `response ${error.status}`
            : "exception",
          message: typeof e?.message === "string" ? e.message : String(error),
          stack: typeof e?.stack === "string" ? e.stack : null,
          path: window.location.pathname + window.location.search,
        }),
      }).catch(() => {});
    } catch {
      // reporting never breaks the error page itself
    }
  }, [error]);

  // Handle 404 errors
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <Layout>
          <ErrorPage
            statusCode={404}
            title="There's nothing here 🔍"
            message="We couldn't find the page that you're looking for!"
          />
        </Layout>
      );
    }

    // Handle other HTTP errors
    return (
      <Layout>
        <ErrorPage
          statusCode={error.status}
          title={error.statusText || "Error"}
          message={
            error.data?.message ||
            "An error occurred while processing your request."
          }
        />
      </Layout>
    );
  }

  // Handle general errors
  return (
    <Layout>
      <ErrorPage
        title="Something went wrong"
        message="An unexpected error occurred. Please try again later or contact support if the problem persists."
      />
    </Layout>
  );
};

/**
 * A question to Lucy (the insights/ask action) changes nothing the root loads.
 * ⭐ Every move to ANOTHER PAGE re-reads the root (MC7712): its request-local
 * gate below then sees each page a person with a temporary password opens.
 */
export const shouldRevalidate = ({
  formAction,
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) =>
  formAction && formAction.endsWith("/insights/ask")
    ? false
    : currentUrl.pathname !== nextUrl.pathname || defaultShouldRevalidate;

export async function loader({ request, context }: LoaderFunctionArgs) {
  bindLoaderReadScope(request, context);
  const user = await loadBasicUser(request);
  /**
   * ⛔ A TEMPORARY PASSWORD IS CHANGED FIRST (Haroon, 5 Oct 2026, MC7712):
   * every page but the password page, sign-in/out and the auth API sends the
   * person to /account/password until BetterAuth reports the change. A check
   * that cannot be made is a retry, never a way in.
   */
  const gate = await gateFor(user?.id, new URL(request.url).pathname);
  if (gate === "redirect") throw redirect(CHANGE_PASSWORD_PATH);
  if (gate === "unavailable")
    throw new Response(
      "Your account could not be checked just now. Please try again.",
      { status: 503, headers: { "Retry-After": "5" } }
    );

  return {
    ENV: getEnv(),
    user,
  };
}

function AppContent() {
  const { ENV, user } = useLoaderData<typeof loader>();
  const location = useLocation();
  const { onLocationChange } = useNavigationHistory();

  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const hasOpenModal = document.querySelector(
        [
          "[data-slot='dialog-content'][data-state='open']",
          "[data-slot='alert-dialog-content'][data-state='open']",
          "[data-slot='sheet-content'][data-state='open']",
          "[data-slot='drawer-content'][data-state='open']",
          "[data-slot='popover-content'][data-state='open']",
        ].join(",")
      );

      if (!hasOpenModal && document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.key]);

  React.useEffect(() => {
    const newPath = location.pathname + location.search;
    onLocationChange(newPath);

    initAmplitude();
    setUserId(user?.id ?? null);
    setUserProperties({ name: user?.name ?? undefined });

    if (ENV.SENTRY_DSN) {
      if (user) {
        setUser({
          id: user.id,
          email: user.email ?? undefined,
          username: user.name ?? undefined,
          segment: user.role ?? undefined,
        });
      } else {
        setUser(null);
      }
    }
  }, [location, onLocationChange, user, ENV.SENTRY_DSN]);

  return (
    <FeedbackProvider>
      <Outlet />
      {/* ⛔ Haroon, 8 Sep 2026, live review with Stephanie: NO launchers on any
          served surface — not the feedback button, not the TanStack devtools
          toggle (the "tropical tree"), Hosni included. The feedback system
          itself stays (FeedbackProvider, the dialog, the /feedback route):
          Lucy is the intended feedback channel, the button is hidden, not the
          plumbing. The devtools toggle is gone rather than parked in a corner —
          moving it did not satisfy the request. */}
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(ENV)}`,
        }}
      />
    </FeedbackProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <NavigationHistoryProvider
        initialState={{ previousPath: null, currentPath: null }}
      >
        <AppContent />
      </NavigationHistoryProvider>
    </QueryClientProvider>
  );
}
